import { window } from "global";
import React, { useState, useEffect } from "react";
import classnames from "classnames";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { NewAppDownloadButton } from "components/Buttons/AppDownloadButton";
import AppFeature, {
  AppFeatureProps,
  deadlockFeatureCards,
  LolFeatureCards,
  valFeatureCards,
} from "@ugg/shared/components/common/AppFeature";
import { appFilename } from "lib/file-download";
import { SvgGameLol } from "@ugitgud/legos/ui/icon/SVG/GameLol";
import { SvgDeadlockLogo } from "@ugitgud/legos/ui/icon/SVG/DeadlockLogo";
import { SvgGameVal } from "@ugitgud/legos/ui/icon/SVG/GameVal";
import { SvgGameWow } from "@ugitgud/legos/ui/icon/SVG/GameWow";
import { SvgBrandIcyVeins } from "@ugitgud/legos/ui/icon/SVG/BrandIcyVeins";
import { SvgDownloadWindows } from "@ugitgud/legos/ui/icon/SVG/DownloadWindows";
import { Icon as SvgIcon } from "@ugitgud/legos/ui/icon/Icon";
import { Helmet } from "react-helmet-async";
import { SupportedGames } from "@ugitgud/legos/ui/nav/GameSelector";
import { ReactComponent as RiotIcon } from "@ugg/shared/assets/svg/riot-icon-uncolored.svg";
import { DeadlockFeatureSpotlight } from "components/DesktopAppSplashPage/DeadlockFeatureSpotlight";
import { WoWFeatureSpotlight } from "components/DesktopAppSplashPage/WoWFeatureSpotlight";
import { WoWAppContent } from "components/DesktopAppSplashPage/WoWAppContent";

function toHex(str: string) {
  let h = "";
  for (let i = 0; i < str.length; i++) {
    h += str.charCodeAt(i).toString(16);
  }
  return h;
}

type AppSupportedGames = Extract<SupportedGames, "lol" | "val" | "deadlock" | "wow">;

const UGG_DOWNLOAD_URL = "https://u.gg/get-windows-app";
const UGG_PROMO_VIDEO = "https://static.bigbrain.gg/assets/ugg/pages/desktop-app/ugg_lol_val_promo.webm";
const DEADLOCK_PROMO_VIDEO = "https://static.bigbrain.gg/assets/ugg/pages/desktop-app/deadlock_promo.webm";
const WOW_HERO_BACKGROUND = "https://static.bigbrain.gg/assets/ugg/pages/desktop-app/wow/wow-hero-bg.webp";

const WOW_CLOSING_CTA = [
  "!h-[36px] !min-h-[36px] !w-[240px] !min-w-0 !gap-[8px] !px-[12px] !py-0",
  "[&_.download-btn_icon]:!mr-0 [&_.download-btn_icon]:!h-[18px] [&_.download-btn_icon]:!w-[18px]",
  "[&>span]:!text-[13px]",
].join(" ");

function UggHero() {
  return (
    <>
      <h1>
        <span className="text-[30px]">One app, Four Games</span>
        <br className="max-sm:hidden" />
        <strong className="max-sm:block text-[36px]">Dozens of Features.</strong>
      </h1>
      <h2>
        The <strong className="text-white">new U.GG app</strong> is available, combining our LoL, WoW, Deadlock,{" "}
        <br className="max-sm:hidden" />& Valorant products into one free, seamless experience.
      </h2>
    </>
  );
}

function UggHeroFootnote() {
  return (
    <>
      <a className="block mt-[24px] text-[14px]" href={UGG_DOWNLOAD_URL} download>
        Download button not working? <span className="text-accent-blue-400 underline">Click here.</span>
      </a>
      <div className="flex gap-x-[8px] mt-[24px] items-center">
        <RiotIcon className="w-[20px] h-[20px] text-[#CDDCFE]" />
        <span style={{ fontSize: 14, color: "#CDDCFE" }}>Riot Games Compliant</span>
      </div>
    </>
  );
}

function WoWHero() {
  return (
    <>
      <h1>
        <div className="mb-[6px] text-[36px] font-bold leading-normal">The Icy Veins App is Here</div>
        <div className="text-[30px] font-normal leading-normal">
          Class Guides, Tier Lists, <b className="font-semibold">Class Codex</b>
        </div>
      </h1>
      <div className="mb-[24px] mt-[36px] flex items-start gap-[18px]">
        <SvgBrandIcyVeins className="h-[54px] w-[86px] shrink-0" role="img" aria-label="Icy Veins" />
        <p className="m-0 w-[449px] max-w-full font-inter text-[14px] font-normal leading-[17px] text-lavender-50">
          Everything you love about Icy Veins World of Warcraft, now in a desktop app, along with the new Class Codex addon and
          insights integrated directly in your game client.
        </p>
      </div>
    </>
  );
}

function WoWHeroFootnote() {
  return (
    <>
      <a className="block mt-[24px] text-[14px]" href={UGG_DOWNLOAD_URL} download>
        Download button not working? <span className="text-accent-blue-400 underline">Click here.</span>
      </a>
      <div className="mt-[24px] flex items-center gap-[8px] text-[14px] font-medium text-lavender-50">
        <SvgIcon className="h-[20px] w-[20px]" icon={<SvgDownloadWindows />} />
        <span>Only available on Windows</span>
      </div>
    </>
  );
}

type GameContentProps = {
  downloadButton?: React.ReactNode;
};

type GameBackground =
  | { kind: "video"; src: string }
  // Static image backgrounds span the content area instead of the fixed
  // 923px video box, and skip the overlay (the art bakes in its own fade).
  | { kind: "image"; src: string };

type GameConfig = {
  game: AppSupportedGames;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  beta?: boolean;
  new?: boolean;
  pageTitle: string;
  pageDescription?: string;
  downloadUrl: string;
  background: GameBackground;
  Hero: React.ComponentType;
  HeroFootnote: React.ComponentType;
  headerClassName?: string;
  Spotlight?: React.ComponentType;
  featureCards: AppFeatureProps[];
  Content?: React.ComponentType<GameContentProps>;
  closingCtaClassName?: string;
};

const uggAppBase = {
  pageTitle: "Download U.GG's Free Desktop App",
  pageDescription:
    "Download U.GG's free desktop app for LoL, WoW, Deadlock, & Valorant builds, stats, and overlays in one seamless experience.",
  downloadUrl: UGG_DOWNLOAD_URL,
  background: { kind: "video", src: UGG_PROMO_VIDEO } satisfies GameBackground,
  Hero: UggHero,
  HeroFootnote: UggHeroFootnote,
};

const gameConfigs: Record<AppSupportedGames, GameConfig> = {
  lol: {
    ...uggAppBase,
    game: "lol",
    label: "League of Legends",
    Icon: SvgGameLol,
    featureCards: LolFeatureCards,
  },
  val: {
    ...uggAppBase,
    game: "val",
    label: "Valorant",
    Icon: SvgGameVal,
    featureCards: valFeatureCards,
  },
  deadlock: {
    ...uggAppBase,
    game: "deadlock",
    label: "Deadlock",
    Icon: SvgDeadlockLogo,
    background: { kind: "video", src: DEADLOCK_PROMO_VIDEO },
    featureCards: deadlockFeatureCards,
    Spotlight: DeadlockFeatureSpotlight,
  },
  wow: {
    game: "wow",
    label: "World of Warcraft",
    Icon: SvgGameWow,
    new: true,
    pageTitle: "Icy Veins App",
    downloadUrl: UGG_DOWNLOAD_URL,
    background: { kind: "image", src: WOW_HERO_BACKGROUND },
    Hero: WoWHero,
    HeroFootnote: WoWHeroFootnote,
    headerClassName: "!mb-[84px]",
    Spotlight: WoWFeatureSpotlight,
    featureCards: [],
    Content: WoWAppContent,
    closingCtaClassName: WOW_CLOSING_CTA,
  },
};

type DesktopSplashPageProps = {
  game?: AppSupportedGames;
};

export const DesktopAppSplashPage: React.FC<DesktopSplashPageProps> = () => {
  const queryString = useQueryString();
  const gameFromQuery = (queryString.game as AppSupportedGames) || "lol";
  const [tab, setTab] = useState<AppSupportedGames>(gameFromQuery);

  const config = gameConfigs[tab] || gameConfigs.lol;
  const {
    pageTitle,
    pageDescription,
    downloadUrl,
    background,
    Hero,
    HeroFootnote,
    headerClassName,
    Spotlight,
    featureCards,
    Content,
    label,
  } = config;

  useEffect(() => {
    window?.scrollTo(0, 0);
    // Remove skin ads
    try {
      window.top.document.getElementById("siteSkinContainer").remove();
    } catch (e) {}
  }, []);

  const renderDownloadButton = (className?: string) =>
    window ? (
      <NewAppDownloadButton
        className={className}
        url={downloadUrl}
        filename={appFilename(toHex((queryString.utm_campaign || "").toLowerCase()))}
        label="Download App"
        icon="cloud"
      />
    ) : null;

  return (
    <div className="desktop-app-splash-page">
      <Helmet>
        <title>{pageTitle}</title>
        {pageDescription && <meta name="description" content={pageDescription} />}
      </Helmet>
      <div className="desktop-app-splash-page_width-container">
        <div className={classnames("video-bg", { "!left-0 !h-[960px] !w-full !transform-none": background.kind === "image" })}>
          {background.kind === "video" ? (
            <>
              <div className="overlay" />
              <video key={tab} playsInline autoPlay loop muted>
                <source type="video/webm" src={background.src} />
              </video>
            </>
          ) : (
            <div className="h-full w-full bg-cover bg-top bg-no-repeat" style={{ backgroundImage: `url(${background.src})` }} />
          )}
        </div>
        <div className={classnames("desktop-app-splash-page_header", headerClassName)}>
          <Hero />
          <div className="download-row">{renderDownloadButton()}</div>
          <HeroFootnote />
        </div>
        <div className="app-tabs mt-[18px] flex flex-wrap items-center gap-[12px]">
          {Object.values(gameConfigs).map((appTab) => {
            return (
              <GameAppTab
                key={appTab.game}
                {...appTab}
                isActive={tab === appTab.game}
                onClick={() => setTab(appTab.game)}
                isNew={appTab.new}
              />
            );
          })}
        </div>
        {Spotlight && <Spotlight />}
        {featureCards.length > 0 && (
          <div className="desktop-app-splash-page_features-container">
            <div className="features-container_header">
              <h1>{label} Features</h1>
            </div>
            <div className="features-grid">
              {featureCards.map((feature, index) => {
                return <AppFeature key={index} {...feature} />;
              })}
            </div>
          </div>
        )}
        {Content && <Content downloadButton={renderDownloadButton(config.closingCtaClassName)} />}
      </div>
    </div>
  );
};

type GameAppTabProps = {
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isActive: boolean;
  beta?: boolean;
  isNew?: boolean;
  onClick: () => void;
};

const GameAppTab: React.FC<GameAppTabProps> = ({ label, Icon, beta, isNew, isActive, onClick }) => {
  const [hover, setHover] = useState(false);

  return (
    <button
      className={classnames("flex items-center justify-center px-[12px] py-[10px] rounded-[3px] bg-purple-400 font-['Inter']", {
        "!bg-purple-100": hover,
        "!bg-purple-100 border-t-[1px] border-[#575792]": isActive,
      })}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      {isActive || hover ? (
        <SvgIcon className="w-[18px]" icon={<Icon />} />
      ) : (
        <SvgIcon className="w-[18px] text-accent-gray-100" icon={<Icon />} />
      )}
      <div
        className={classnames("ml-[8px] text-[13px] text-lavender-50", { "text-white font-bold": isActive, "text-white": hover })}
      >
        {label}
      </div>
      {beta && (
        <div className="flex items-center ml-[8px] px-[4px] h-[14px] rounded-[1px] bg-accent-forest-green-500 font-bold text-[10px]">
          BETA
        </div>
      )}
      {isNew && (
        <div className="flex items-center ml-[8px] px-[4px] h-[14px] rounded-[1px] bg-accent-blue-400 font-bold text-[10px]">
          NEW
        </div>
      )}
    </button>
  );
};

export default DesktopAppSplashPage;
